import React, { useState, useRef, useEffect, useCallback } from 'react';
import IngredientCard from './IngredientCard';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import Image1 from '../../../assets/carousel/1.webp';
import Image2 from '../../../assets/carousel/2.webp';
import Image3 from '../../../assets/carousel/3.webp';
import Image4 from '../../../assets/carousel/4.webp';
import Image5 from '../../../assets/carousel/5.webp';
import Image6 from '../../../assets/carousel/6.webp';
import Image1f from '../../../assets/carousel/1f.webp';
import Image2f from '../../../assets/carousel/2f.webp';
import Image3f from '../../../assets/carousel/3f.webp';
import Image4f from '../../../assets/carousel/4f.webp';
import Image5f from '../../../assets/carousel/5f.webp';
import Image6f from '../../../assets/carousel/6f.webp';
import Image1g from '../../../assets/carousel/1g.webp';
import Image2g from '../../../assets/carousel/2g.webp';
import Image3g from '../../../assets/carousel/3g.webp';
import Image4g from '../../../assets/carousel/4g.webp';
import Image5g from '../../../assets/carousel/5g.webp';
import Image6g from '../../../assets/carousel/6g.webp';
import Image7 from '../../../assets/carousel/7.webp';
import Image7f from '../../../assets/carousel/7f.webp';
import Image7g from '../../../assets/carousel/7g.webp';
import Image8 from '../../../assets/carousel/8.webp';
import Image8f from '../../../assets/carousel/8f.webp';
import Image8g from '../../../assets/carousel/8g.webp';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../../features/langSlice';
import { Icon } from '../../elements/icon';
import { useTranslation } from 'react-i18next';
import { isGermanLanguage } from '../../../utils/language';
import { getPersonalFormulaTitle } from '../../../utils/product-copy';
import {
  IncludesCarousel,
  NutrientsCarousel,
  ViewFormula,
  Ingredients,
} from '../../../Constants';
import FormulaSS from '../../../assets/formula-ss.png';
import SlideCanvas from './ProductCarousel/SlideCanvas';
import useProductCarouselSlides from './ProductCarousel/useProductCarouselSlides';
export const DEFAULT_CAROUSEL_IMAGES = [
  { en: Image1, de: Image1g, fr: Image1f },
  { en: Image2, de: Image2g, fr: Image2f },
  { en: Image3, de: Image3g, fr: Image3f },
  { en: Image6, de: Image6g, fr: Image6f },
  { en: Image4, de: Image4g, fr: Image4f },
  { en: Image5, de: Image5g, fr: Image5f },
  { en: Image7, de: Image7g, fr: Image7f },
  { en: Image8, de: Image8g, fr: Image8f },
];

const BLANK_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const getLocalizedImage = (image, language) => image?.[language] || image?.en || image?.de || image?.fr;

const CarouselProduct = ({
  data = [],
  allIngredients,
  onClickViewFormula,
  name,
  isCustomFormula,
  focusAreaCount = 0,
  personalSignalsCount = 0,
  specialConditionsCount = 0,
  medicationsCount = 0,
  images = DEFAULT_CAROUSEL_IMAGES,
  // Product-suggestion page only: { planDuration, mainPurpose, ingredients, focusAreas, onEditFormula }.
  // When set, the new slides replace `images` (see ProductCarousel/).
  suggestion,
}) => {
  const selectedLang = useSelector(selectCurrentLanguage);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formulaLayoutVersion, setFormulaLayoutVersion] = useState(0);
  const slides = useProductCarouselSlides(suggestion);
  const isSuggestion = slides.length > 0;
  const extraSlideCount = isSuggestion ? slides.length : images.length;
  const previousSlideKeysRef = useRef([]);
  const { t, i18n } = useTranslation();
  const isGerman = isGermanLanguage(i18n.language);
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
  const formulaResizeFrameRef = useRef(null);

  // Thumbnail strip: single side-scrollable row with desktop arrow affordances.
  const thumbStripRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateThumbArrows = useCallback(() => {
    const el = thumbStripRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    updateThumbArrows();
    const handleResize = () => updateThumbArrows();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateThumbArrows]);

  useEffect(() => {
    if (!isSuggestion) return;
    const keys = slides.map((s) => s.key);
    const previousKeys = previousSlideKeysRef.current;
    previousSlideKeysRef.current = keys;
    if (previousKeys.length === 0 || previousKeys.join('|') === keys.join('|')) return;
    setCurrentSlide((prev) => {
      const index = prev > 0 ? keys.indexOf(previousKeys[prev - 1]) : -1;
      return index >= 0 ? index + 1 : 0;
    });
  }, [isSuggestion, slides]);

  useEffect(
    () => () => {
      if (formulaResizeFrameRef.current) cancelAnimationFrame(formulaResizeFrameRef.current);
    },
    [],
  );

  const handleIngredientToggle = useCallback(() => {
    if (formulaResizeFrameRef.current) cancelAnimationFrame(formulaResizeFrameRef.current);

    formulaResizeFrameRef.current = requestAnimationFrame(() => {
      setFormulaLayoutVersion((version) => version + 1);
      formulaResizeFrameRef.current = null;
    });
  }, []);

  // Translate a vertical mouse wheel into horizontal scrolling over the strip so
  // mouse users (no horizontal wheel) can scroll the thumbnails. Trackpad horizontal
  // swipes send deltaX and are left to the browser's native handling. At either edge
  // we bail out so the page can scroll normally.
  useEffect(() => {
    const el = thumbStripRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= maxScroll - 1;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const scrollThumbs = (dir) => {
    const el = thumbStripRef.current;
    if (!el) return;
    const start = el.scrollLeft;
    const target = start + dir * 240;
    const duration = 300;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      el.scrollLeft = start + (target - start) * eased;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // Mobile opens a single card: the first preview ingredient with a personalized
  // (non-static) description. A description is "static" when no AI copy was generated
  // and it fell back to the catalog text (description === static_description). If every
  // previewed ingredient is static, fall back to opening the first one.
  const previewItems = data?.slice(0, 5) || [];
  const firstNonStatic = previewItems.findIndex(
    (it) => it?.description && it?.static_description && it.description !== it.static_description,
  );
  const mobileOpenIndex = firstNonStatic === -1 ? 0 : firstNonStatic;

  return (
    <div className="t-relative t-flex t-flex-col">
      <div className="t-rounded-lg t-overflow-hidden t-border t-border-g200">
        <Carousel
          selectedItem={currentSlide}
          onChange={(index) => setCurrentSlide(index)}
          showArrows={true}
          showIndicators={false}
          showThumbs={false}
          showStatus={false}
          dynamicHeight={true}
          swipeable={false}
          preventMovementUntilSwipeScrollTolerance={true}
          swipeScollTolerance={100}
          // className="container"
          renderArrowPrev={() => {
            const totalSlides = 1 + extraSlideCount;
            return (
              <Icon
                name="chevronLeft"
                onClick={() => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)}
                className="t-absolute -t-left-1 t-top-[45%] t-rounded-[0_8px_8px_0] t-p-1 t-w-8 t-h-8 t-z-10 t-bg-white t-text-navy t-shadow-md hover:t-bg-g100 t-cursor-pointer"
              />
            );
          }}
          renderArrowNext={() => {
            const totalSlides = 1 + extraSlideCount;
            return (
              <Icon
                name="chevronRight"
                onClick={() => setCurrentSlide((prev) => (prev + 1) % totalSlides)}
                className="t-absolute -t-right-1 t-top-[45%] t-rounded-[8px_0_0_8px] t-p-1 t-w-8 t-h-8 t-z-10 t-bg-white t-text-navy t-shadow-md hover:t-bg-g100 t-cursor-pointer"
              />
            );
          }}
        >
          <div
            data-layout-version={formulaLayoutVersion}
            className="!t-w-full t-min-h-[420px] md:t-min-h-0 md:t-h-auto t-flex t-flex-col t-bg-white t-text-left"
          >
            {/* Header */}
            <div className="t-flex t-items-center t-justify-between t-gap-2 t-px-4 t-pt-3 t-pb-2">
              <div>
                <h1 className="!t-m-0 t-font-serif t-font-normal t-text-navy !t-text-[20px] !t-text-left">
                  {getPersonalFormulaTitle(t, name, i18n.language)}
                </h1>
                <p className="!t-m-0 t-font-sans t-text-[12px] t-text-navy/60">
                  {t(IncludesCarousel)} {data?.length} {t(NutrientsCarousel)}
                </p>
              </div>
              <button
                className="t-bg-navy t-text-white t-border-0 t-rounded-lg t-px-3 t-py-1.5 t-font-sans t-text-[13px] t-font-semibold t-cursor-pointer hover:t-opacity-90 t-transition-opacity t-flex-shrink-0"
                onClick={onClickViewFormula}
              >
                {t(ViewFormula)}
              </button>
            </div>

            {/* Banner */}
            <div className="t-bg-navy/5 t-border-y t-border-navy/10 t-px-4 t-py-2">
              <p className="!t-m-0 !t-mb-0.5 t-text-[12px] t-font-semibold t-text-navy">
                {t('Carousel_BasedOnQuizAnswers')}
              </p>
              <p className="!t-m-0 t-text-[12px] t-text-navy/70">
                {focusAreaCount > 0 && personalSignalsCount > 0
                  ? (() => {
                      const parts = [
                        {
                          count: focusAreaCount,
                          singular: 'Carousel_HealthGoalSingular',
                          plural: 'Carousel_HealthGoalPlural',
                        },
                        {
                          count: personalSignalsCount,
                          singular: 'Carousel_PersonalSignalSingular',
                          plural: 'Carousel_PersonalSignalPlural',
                        },
                      ];
                      if (specialConditionsCount > 0)
                        parts.push({
                          count: specialConditionsCount,
                          singular: 'Carousel_SpecialConditionSingular',
                          plural: 'Carousel_SpecialConditionPlural',
                        });
                      if (medicationsCount > 0)
                        parts.push({
                          count: medicationsCount,
                          singular: 'Carousel_MedicationSingular',
                          plural: 'Carousel_MedicationPlural',
                        });
                      return (
                        <>
                          {t('Carousel_BuiltAroundPrefix')}{' '}
                          {parts.map((p, i) => {
                            const sep =
                              i === 0 ? '' : i === parts.length - 1 ? ` ${t('Carousel_And')} ` : ', ';
                            return (
                              <React.Fragment key={p.singular}>
                                {sep}
                                <strong>
                                  {p.count} {p.count === 1 ? t(p.singular) : t(p.plural)}
                                </strong>
                              </React.Fragment>
                            );
                          })}
                          . {t('Carousel_EachIngredientReason')}
                        </>
                      );
                    })()
                  : t('Carousel_FallbackBanner')}
              </p>
            </div>

            {/* Ingredient cards */}
            {data?.length === 0 && isCustomFormula ? (
              <div className="t-h-[250px] t-flex t-flex-col t-items-center t-justify-center t-gap-3 t-px-4">
                <h5 className="t-w-[80%] t-text-center t-text-base t-font-sans t-text-navy !t-m-0">
                  {t('Carousel_NoIngredientsMessage')}
                </h5>
                <button
                  className="t-bg-quaternary t-text-white t-border-0 t-rounded-lg t-px-3 t-py-1.5 t-font-sans t-text-[13px] t-font-semibold t-cursor-pointer hover:t-opacity-90"
                  onClick={onClickViewFormula}
                >
                  {t('Carousel_AddButton')}
                </button>
              </div>
            ) : data?.length === 0 ? (
              <div className="t-h-[200px]" />
            ) : (
              <div className="t-px-3 t-pt-2 t-pb-1">
                <div className="t-flex t-items-center t-gap-3 t-px-3 t-pb-1">
                  <div className="t-w-2.5" />
                  <div className="t-flex-1" />
                  <div className="t-flex t-items-center t-gap-2 t-flex-shrink-0">
                    <span className="t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider t-min-w-[44px] t-text-right">
                      {t('Quantity')}
                    </span>
                    <span className="t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider t-min-w-[28px] t-text-right">
                      {isGerman ? t('OrderDetails_Col_NRV') : '%NRV'}
                    </span>
                    <span className="t-w-2.5" />
                  </div>
                </div>
                {data?.slice(0, 5).map((item, i) => {
                  const ingredient = allIngredients?.data?.find(
                    (ing) => ing.ingredient === item?.ingredient || ing.ingredient_de === item?.ingredient,
                  );
                  const ingredientData = ingredient;
                  return (
                    <IngredientCard
                      key={'carousel-card-' + item?.ingredient}
                      item={item}
                      index={i}
                      ingredientData={ingredientData}
                      defaultOpen={isDesktop ? i === 0 : i === mobileOpenIndex}
                      onToggle={handleIngredientToggle}
                    />
                  );
                })}
              </div>
            )}

            <button
              onClick={onClickViewFormula}
              className="!t-mx-auto t-font-sans t-text-[13px] t-font-semibold t-text-navy t-flex t-flex-row t-justify-center t-items-center t-py-2 t-gap-1 t-bg-transparent t-border-0 t-border-t t-border-g200 t-w-full t-cursor-pointer hover:t-opacity-70 t-transition-opacity"
            >
              {isGerman
                ? t('Carousel_ViewAllWithCount', { count: data?.length || 0 })
                : <>{t('Carousel_ViewAll')} {data?.length} {t(Ingredients)} →</>}
            </button>
          </div>
          {isSuggestion
            ? slides.map((slide) => (
                <div key={'carousel-slide-' + slide.key} className="t-relative">
                  {/* react-responsive-carousel sizes dynamicHeight from the first <img> of a slide: this probe has the slide's aspect ratio */}
                  <img src={BLANK_PIXEL} alt="" className="pc-height-probe" />
                  <SlideCanvas fill>{slide.node}</SlideCanvas>
                </div>
              ))
            : images.map((img, index) => (
            <div key={'carousel-img-' + index} className="t-min-h-[420px] md:t-min-h-0 md:t-h-auto">
              <img
                src={getLocalizedImage(img, selectedLang)}
                alt=""
                className="t-w-full md:t-h-auto t-h-full t-object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
            </div>
          ))}
        </Carousel>
      </div>
      <div className="t-relative t-mt-4">
        {canScrollLeft && (
          <Icon
            name="chevronLeft"
            onClick={() => scrollThumbs(-1)}
            className="t-hidden lg:t-flex t-absolute t-left-0 t-top-1/2 -t-translate-y-1/2 t-z-10 t-items-center t-justify-center t-p-1 t-w-8 t-h-8 t-rounded-full t-bg-white t-text-navy t-shadow-md hover:t-bg-g100 t-cursor-pointer"
          />
        )}
        {canScrollRight && (
          <Icon
            name="chevronRight"
            onClick={() => scrollThumbs(1)}
            className="t-hidden lg:t-flex t-absolute t-right-0 t-top-1/2 -t-translate-y-1/2 t-z-10 t-items-center t-justify-center t-p-1 t-w-8 t-h-8 t-rounded-full t-bg-white t-text-navy t-shadow-md hover:t-bg-g100 t-cursor-pointer"
          />
        )}
        <div
          ref={thumbStripRef}
          onScroll={updateThumbArrows}
          className="t-flex t-flex-nowrap t-gap-2 t-overflow-x-auto hide-scrollbar t-bg-surface"
        >
          <div
            onClick={() => setCurrentSlide(0)}
            className={`t-h-12 t-w-12 lg:t-w-20 lg:t-h-20 t-flex t-items-center t-justify-center t-bg-surface t-border-2 t-rounded t-cursor-pointer t-flex-shrink-0 ${
              currentSlide === 0
                ? 't-border-navy t-opacity-100'
                : 't-border-transparent t-opacity-60 hover:t-opacity-100'
            }`}
          >
            <img
              src={FormulaSS}
              alt=""
              className="t-h-full t-w-full t-object-cover t-flex-shrink-0"
              style={{ mixBlendMode: 'multiply' }}
            />
          </div>

          {isSuggestion
            ? slides.map((slide, index) => {
                const actualIndex = index + 1;
                const isActive = actualIndex === currentSlide;
                return (
                  <div
                    key={'sub-slide-' + slide.key}
                    onClick={() => setCurrentSlide(actualIndex)}
                    className={`t-h-12 t-w-12 lg:t-w-20 lg:t-h-20 t-flex-shrink-0 t-bg-surface t-border-2 t-rounded t-cursor-pointer t-transition-all t-overflow-hidden t-flex t-items-center ${
                      isActive
                        ? 't-border-navy t-opacity-100'
                        : 't-border-transparent t-opacity-60 hover:t-opacity-100'
                    }`}
                  >
                    <div className="t-w-full">
                      <SlideCanvas thumb>{slide.node}</SlideCanvas>
                    </div>
                  </div>
                );
              })
            : images.map((img, index) => {
                const actualIndex = index + 1;
                const isActive = actualIndex === currentSlide;
                return (
                  <div
                    key={'sub-img-' + index}
                    onClick={() => setCurrentSlide(actualIndex)}
                    className={`t-h-12 t-w-12 lg:t-w-20 lg:t-h-20 t-flex-shrink-0 t-bg-surface t-border-2 t-rounded t-cursor-pointer t-transition-all t-overflow-hidden ${
                      isActive
                        ? 't-border-navy t-opacity-100'
                        : 't-border-transparent t-opacity-60 hover:t-opacity-100'
                    }`}
                  >
                    <img
                      src={getLocalizedImage(img, selectedLang)}
                      alt={`Thumb ${index + 1}`}
                      className="t-h-full t-w-full t-object-cover"
                      style={{ mixBlendMode: 'multiply' }}
                    />
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
};

export default CarouselProduct;
